import { IOfferAudience } from './offerAudience';
import { pluralize } from '@/utils';
import store from '@/store';
import { ILocation } from '@/models/location';
import { app } from '@/main';
import DateUtils from '@/utils/DateUtils';
import { uses24HourClock } from '@/utils/clockFormat';
import { VueMultiselectOptions } from './select-dropdown';
import { PromoCodeOption } from '@/views/Offers/OfferCreation/Steps/FormOffer/FormOffer.vue';

export enum IOfferDiscountTypeName {
  OPEN_TEXT_FIELD = 'open_text_field',
  AMOUNT = 'money_discount',
  PERCENTAGE = 'percent_discount'
}

export type TSpecialCoinOfferType =
  | 'birthday'
  | 'specific-product'
  | 'general-product';

export enum ESpecialCoinOfferType {
  BIRTHDAY = 'birthday',
  SPECIFIC = 'specific-product',
  GENERAL = 'general-product'
}

export enum IOfferType {
  BIRTHDAY = 'birthday',
  GENERAL = 'general'
}

export enum EOfferTypeEnum {
  GENERAL = 'General',
  BIRTHDAY = 'Birthday',
  STOREWIDE = 'ProductAndService',
  HAPPY_HOUR = 'HappyHour'
}
export enum SpecialOfferTypeEnum {
  STOREWIDE = 'Storewide',
  BIRTHDAY = 'Birthday',
  PRODUCT_AND_SERVICE = 'ProductAndService',
  TWO_FOR_ONE = 'TwoForOne',
  HAPPY_HOUR = 'HappyHour'
}

export enum IOfferProductType {
  SPECIFIC = 'specific-product',
  GENERAL = 'general-product'
}

export enum IOfferMethod {
  ONLINE = 'online',
  INSTORE = 'offline',
  BOTH = 'offline_online'
}

export enum IOfferDiscountType {
  AMOUNT = 'amount',
  PERCENTAGE = 'percentage'
}

export enum EOfferStatusEnum {
  ACTIVE = 'active',
  EXPIRED = 'expired',
  ARCHIVED = 'archived',
  INACTIVE = 'inactive',
  DELETED = 'deleted',
  SOLDOUT = 'soldout'
}

export enum ESpecialOfferRules {
  SUPPLY_RULE = 'offer-rule-supply',
  EXPIRATION_RULE = 'offer-rule-expiration',
  LIMIT_RULE = 'offer-rule-limit',
  COMBINED_RULE = 'offer-rule-combined',
  RENEWED_RULE = 'offer-rule-renewed'
}

export enum EOfferRulesName {
  SUPPLY_RULE = 'max_redeemed_items',
  LIMIT_RULE = 'max_redeemed_items_per_customers',
  COMBINE_RULE = 'cannot_combine_offers',
  ID_CARD_REQUIRED = 'birthday_required_id',
  VALID_PERIOD_DAYS = 'birthday_valid_days',
  RENEWED_RULE = 'max_redeemed_items_per_customers_renew',
  REDEEMABLE_DAY_RULE = 'redemption_days_of_week',
  REDEEMABLE_HOUR_RULE = 'redemption_available_time',
  TWO_FOR_ONE_RULE = 'two_for_one'
}

export type IOfferRule = { name: string; value: string };

export type IOfferDiscount = {
  typeName: IOfferDiscountTypeName;
  value: string;
  originalValue: string;
};

export type IOfferRuleLabel = (rule: IOfferRule) => string;

export class OfferPreview {
  constructor(
    public title?: string,
    public productType?: IOfferProductType,
    public valueOffer?: string,
    public typeDiscount?: string,
    public valueDiscount?: string,
    public description?: string,
    public src?: string,
    public rulesOffer?: IOfferRule[],
    public dateToExpirate?: string,
    public hasDateToExpirate?: boolean,
    public method?: IOfferMethod,
    public isBirthday?: boolean
  ) {}
}

export interface IBirthdayOfferCreation {
  title: string;
  description?: string;
  imageId: string;
  imageSrc: string;
  rules: IOfferRule[];
  discount: IOfferDiscount;
}

export interface IFirstOfferCreationData {
  title: string;
  type: IOfferType;
  method: IOfferMethod;
  discount: IOfferDiscount;
  discountCode: string;
  productType: IOfferProductType;
}

export interface IOfferLocationItem {
  id: string;
  label: string;
  checked?: boolean;
}

export interface IOfferDetailsData {
  title: string;
  discountType: IOfferDiscountType;
  discountAmount: string;
  imageId: string;
  imageLink: string;
  originalPrice: string;
  avgDiscountValue?: string;
  savingsCannotBeDetermined?: boolean;
}

export interface IOfferRedemptionData {
  selectedLocations: Array<IOfferLocationItem>;
  redeemMethod: VueMultiselectOptions;
  link: Record<PromoCodeOption, string>;
  discountCode: string;
  promoCodeOption: PromoCodeOption;
}
export type TOfferRule = Record<string, { enabled: boolean; value?: string }>;
export interface IOfferAdvancedOptionsData {
  rules: TOfferRule;
  restrictions: string;
  description: string;
}

export interface IOfferRedemptionRulesSectionData extends IOfferRedemptionData {
  availability: {
    [EOfferRulesName.REDEEMABLE_DAY_RULE]: { enabled: boolean; value: string };
    [EOfferRulesName.REDEEMABLE_HOUR_RULE]: { enabled: boolean; value: string };
  };
  additionalRules: {
    [ESpecialOfferRules.LIMIT_RULE]: { enabled: boolean; value: string };
    [ESpecialOfferRules.RENEWED_RULE]: { enabled: boolean; value: string };
  };
}
export interface ISecondOfferCreationData {
  rules: IOfferRule[];
  expirationDate?: string;
  imageId: string;
  imageSrc: string;
  description?: string;
}

export interface IOfferCreation
  extends IFirstOfferCreationData,
    ISecondOfferCreationData {
  business: string;
}

export interface IOfferEdit
  extends IFirstOfferCreationData,
    ISecondOfferCreationData {
  locations?: ILocation[];
  restrictions?: string;
  image?: {
    id: string;
    url: string;
  };
  tag?: string;
  id: string;
  specialType: SpecialOfferTypeEnum;
  avgDiscountValue?: string;
  audience?: IOfferAudience | null;
  savingsCannotBeDetermined?: boolean;
}

export const BirthdayDefaultParameters = {
  discount: {
    typeName: IOfferDiscountTypeName.OPEN_TEXT_FIELD,
    value: 'Birthday Club',
    originalValue: '0'
  },
  rules: [
    {
      name: EOfferRulesName.COMBINE_RULE,
      value: 'true'
    },
    {
      name: EOfferRulesName.ID_CARD_REQUIRED,
      value: ''
    },
    {
      name: EOfferRulesName.VALID_PERIOD_DAYS,
      value: ''
    }
  ]
};

export const OfferRulesValidNames = [
  EOfferRulesName.SUPPLY_RULE,
  EOfferRulesName.COMBINE_RULE,
  EOfferRulesName.LIMIT_RULE
];

export const getRenewedValue = (value: string) => {
  const [period, time] = value.split(':');

  const periodFormat: any = {
    yearly: app.$tc('common.pluralize.year', 1),
    monthly: app.$tc('common.pluralize.month', Number(time)),
    weekly: app.$tc('common.pluralize.week', Number(time)),
    daily: app.$tc('common.pluralize.day', Number(time))
  };
  return {
    period,
    time: time || '1',
    timeNumber: time,
    periodFormatted: periodFormat[period]
  };
};

export const getRenewedLabel = (rule: any) => {
  if (!rule) return 'It will not be renewed';

  const data = getRenewedValue(rule.value);

  return `It will be renewed every ${
    Number(data.time) === 1 ? '' : ` ${data.time}`
  } ${data.periodFormatted} after the redemption`;
};

export const getMaxRedmeedLabel = (rule: any) => {
  if (!rule) return '';

  return `${rule.value} remaining`;
};

export const transformOldOfferTypeToNew = (type: ESpecialCoinOfferType) => {
  const oldTypeMapper: Record<ESpecialCoinOfferType, SpecialOfferTypeEnum> = {
    [ESpecialCoinOfferType.BIRTHDAY]: SpecialOfferTypeEnum.BIRTHDAY,
    [ESpecialCoinOfferType.SPECIFIC]: SpecialOfferTypeEnum.PRODUCT_AND_SERVICE,
    [ESpecialCoinOfferType.GENERAL]: SpecialOfferTypeEnum.STOREWIDE
  };
  return oldTypeMapper[type];
};

export const getOfferTagByType = (
  type: SpecialOfferTypeEnum,
  discount?: string
) => {
  const mapper: Record<SpecialOfferTypeEnum, string> = {
    [SpecialOfferTypeEnum.STOREWIDE]: 'Promo',
    [SpecialOfferTypeEnum.BIRTHDAY]: 'Birthday Offer',
    [SpecialOfferTypeEnum.TWO_FOR_ONE]: '2 for 1',
    [SpecialOfferTypeEnum.PRODUCT_AND_SERVICE]: discount || '',
    [SpecialOfferTypeEnum.HAPPY_HOUR]: 'Happy Hour'
  };
  return mapper[type];
};

export const getExpirationDateLabel = (expirationDate?: string) => {
  if (!expirationDate) return '';

  const now = DateUtils.formattedDate({
    date: new Date(),
    applyTimezone: true
  });

  const expirationDateFromatted = DateUtils.formattedDate({
    date: expirationDate,
    applyTimezone: true
  });

  const diffDays =
    DateUtils.getDifferenceDays(expirationDateFromatted, now) * -1;

  return `${diffDays} ${pluralize(diffDays, 'day')} left`;
};

export const OfferRulesDictionary: Record<string, IOfferRuleLabel> = {
  [EOfferRulesName.SUPPLY_RULE]: rule =>
    `There ${+rule.value > 1 ? 'are' : 'is'} ${rule.value} ${pluralize(
      +rule.value,
      'coupon'
    )} available for this offer.`,
  [EOfferRulesName.LIMIT_RULE]: rule =>
    `There ${+rule.value > 1 ? 'are' : 'is'} a limit of ${
      rule.value
    } ${pluralize(+rule.value, 'coupon')} per user.`,
  [EOfferRulesName.COMBINE_RULE]: rule =>
    rule.value === 'true'
      ? 'This offer cannot be combined with other offers.'
      : '',
  [EOfferRulesName.ID_CARD_REQUIRED]: () =>
    'ID Card required on redeeming Birthday Offers in physical store.',
  [EOfferRulesName.VALID_PERIOD_DAYS]: () =>
    `Offer valid 3 days before and until 3 days after the user's birthday.`,
  [EOfferRulesName.RENEWED_RULE]: rule =>
    `It will be renewed every${
      Number(getRenewedValue(rule.value).time) === 1
        ? ''
        : ` ${getRenewedValue(rule.value).time}`
    } ${getRenewedValue(rule.value).period} after the redemption`
};

export const OfferMethodDictionary: Record<IOfferMethod, string> = {
  [IOfferMethod.BOTH]: 'Both',
  [IOfferMethod.INSTORE]: 'In-Store',
  [IOfferMethod.ONLINE]: 'Online'
};

export const OfferMethodDictionaryV2: Record<IOfferMethod, string> = {
  [IOfferMethod.BOTH]: 'In-Store & Online',
  [IOfferMethod.INSTORE]: 'In-Store only',
  [IOfferMethod.ONLINE]: 'Online only'
};

export const OfferDiscountDictionary: Record<IOfferDiscountType, string> = {
  [IOfferDiscountType.AMOUNT]:
    store.getters['internationalization/getCountrySettings'].currencySymbol,
  [IOfferDiscountType.PERCENTAGE]: '%'
};

const ruleT = (key: string, values?: Record<string, string>): string =>
  String(app.$t(`offers.redeemableRule.${key}`, values));
const dayName = (day: number) => ruleT(`days.${day}`);
const daysName = (day: number) => ruleT(`daysPlural.${day}`);

function buildDayPart(days: number[]): string {
  if (days.length === 0 || days.length === 7) {
    return ruleT('anyDay');
  }

  if (days.length === 5 && days.every(d => d >= 1 && d <= 5)) {
    return ruleT('weekdays');
  }

  if (days.length === 2 && days.includes(0) && days.includes(6)) {
    return ruleT('weekends');
  }

  const ranges = groupConsecutive(days);

  // Single range
  if (ranges.length === 1) {
    const [range] = ranges;

    if (range.length === 1) {
      return ruleT('on', { days: daysName(range[0]) });
    }

    if (range.length === 2) {
      return ruleT('on', {
        days: `${daysName(range[0])} ${ruleT('and')} ${daysName(range[1])}`
      });
    }

    return ruleT('fromThrough', {
      from: dayName(range[0]),
      to: dayName(range[range.length - 1])
    });
  }

  // Multiple ranges
  const parts = ranges.map(range => {
    if (range.length === 1) {
      return daysName(range[0]);
    }

    if (range.length === 2) {
      // 🔥 IMPORTANT: no "and" here
      return `${daysName(range[0])}, ${daysName(range[1])}`;
    }

    // 🔥 IMPORTANT: ranges stay singular
    return ruleT('through', {
      from: dayName(range[0]),
      to: dayName(range[range.length - 1])
    });
  });

  // Flatten comma-separated pairs
  const flattened = parts.flatMap(part => part.split(', '));

  return ruleT('on', { days: joinWithAnd(flattened) });
}

function buildHourPart(hourRule?: string): string {
  if (!hourRule || hourRule.includes('--')) {
    return ruleT('allDayLong');
  }

  const [start, end] = hourRule.split(',').map(s => s.trim());

  if (!start || !end || (start === '00:00:00' && end === '23:59:59')) {
    return ruleT('allDayLong');
  }

  return ruleT('between', { start: formatTime(start), end: formatTime(end) });
}

function formatTime(time: string): string {
  const [h, m] = time.split(':').map(Number);
  const minutes = m.toString().padStart(2, '0');
  if (uses24HourClock(app.$i18n.locale)) {
    return `${h.toString().padStart(2, '0')}:${minutes}`;
  }
  const ampm = h >= 12 ? 'pm' : 'am';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${minutes}${ampm}`;
}

function groupConsecutive(days: number[]): number[][] {
  const groups: number[][] = [];
  let current: number[] = [];

  for (const day of days) {
    if (!current.length || day === current[current.length - 1] + 1) {
      current.push(day);
    } else {
      groups.push(current);
      current = [day];
    }
  }

  if (current.length) groups.push(current);
  return groups;
}

function joinWithAnd(items: string[]): string {
  const and = ruleT('and');
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} ${and} ${items[1]}`;
  return `${items.slice(0, -1).join(', ')} ${and} ${items[items.length - 1]}`;
}

export function getRedeemableString(
  dayRule?: string,
  hourRule?: string,
  prefix = ruleT('prefix')
): string {
  const dayIndexes = dayRule
    ? dayRule
        .split(',')
        .map(s => Number(s.trim()))
        .filter(n => !Number.isNaN(n))
        .sort((a, b) => a - b)
    : [];

  const dayPart = buildDayPart(dayIndexes);
  const hourPart = buildHourPart(hourRule);

  return `${prefix} ${dayPart}${hourPart ? `, ${hourPart}` : ''}`;
}
export interface IOfferListItem {
  id: string;
  title: string;
  description?: string;
  image: string;
  branchLink: string;
  business: any;
  method?: IOfferMethod;
  rulesOffer?: IOfferRule[];
  expirationDate?: string;
  discount: IOfferDiscount;
  discountCode: string;
  productType: IOfferProductType;
  isBirthday?: boolean;
  redeemsCounter?: number;
  sharedCounter?: number;
  savedCounter?: number;
  viewsCounter?: number;
  specialType?: SpecialOfferTypeEnum;
  locations?: ILocation;
}
